import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import IconSvg, { Icons } from 'src/assets/icons/IconSvg';
import IconButton from 'src/components/button/IconButton';
import RaffleCard from 'src/components/cards/RaffleCard';
import RaffleFilterMenu from 'src/components/dropdowns/RaffleFilterMenu';
import SortByMenu from 'src/components/dropdowns/SortByMenu';
import SearchCollectionInput from 'src/components/input/SearchCollectionInput';
import { useViewport } from 'src/context/ViewportContext';
import { collectionDetailRaffles } from 'src/dummyData';

const CollectionDetail = () => {
  const [activeView, setActiveView] = useState('grid');
  const navigate = useNavigate();
  const { isMobile } = useViewport();

  const handleViewClick = (view: string) => {
    setActiveView(view);
  };

  const goBack = () => {
    navigate(-1);
  };

  const views = [
    { key: 'list', icon: isMobile ? 'listS' : 'listM' },
    { key: 'grid', icon: isMobile ? 'gridS' : 'gridM' },
  ];

  return (
    <>
      <section>
        <div className="flex justify-between items-center mb-6 xl:mb-8">
          <IconButton
            size={isMobile ? 'xs' : 'sm'}
            variant="transparent"
            icon="arrowLeftS"
            onClick={goBack}
          >
            Go Back
          </IconButton>

          <div className="hidden gap-3 items-center xl:flex">
            <div className="flex rounded-md bg-grayscale-900 w-fit">
              {views.map(view => (
                <button
                  key={view.key}
                  className={`p-2.5 flex items-center justify-center transition-all duration-200 w-fit ${
                    activeView === view.key
                      ? 'bg-grayscale-800 text-brand-tangerine rounded-md'
                      : 'text-grayscale-500'
                  }`}
                  onClick={() => handleViewClick(view.key)}
                >
                  <IconSvg icon={view.icon as Icons} />
                </button>
              ))}
            </div>

            <SearchCollectionInput />

            <SortByMenu />

            <RaffleFilterMenu />
          </div>
        </div>

        <div className="flex flex-col flex-wrap gap-3 mb-6 xl:mb-8 lg:items-center lg:justify-between xl:gap-4 lg:flex-row">
          <div className="flex gap-4 items-center">
            <div className="w-[48px] xl:w-[88px] h-[48px] xl:h-[88px] bg-white rounded-full"></div>

            <div className="space-y-1">
              <div className="flex gap-2 items-center">
                <h3 className="text-brand-magnolia text-[22px] xl:text-[32px]">Collection Name</h3>

                <IconSvg icon="verifiedM" className="text-brand-tangerine" />
              </div>

              <div className="flex gap-1 items-center text-brand-granite">
                <IconSvg icon={isMobile ? 'discordS' : 'discordM'} />
                <IconSvg icon={isMobile ? 'twitterS' : 'twitterM'} />
                <IconSvg icon={isMobile ? 'webS' : 'webM'} />
              </div>
            </div>
          </div>

          <div className="flex overflow-x-auto gap-3 w-full lg:w-fit no-scrollbar">
            <div className="xl:w-[172px] rounded-lg xl:px-5 xl:py-4 p-3 bg-grayscale-800 space-y-1 flex-shrink-0">
              <div className="text-xs xl:text-sm text-grayscale-400">Floor Price</div>

              <div className="flex gap-2 items-center">
                <IconSvg icon="bitcoinM" />

                <span className="text-brand-magnolia font-medium text-lg xl:text-[24px] tracking-[-0.1px]">
                  0.029
                </span>
              </div>

              <div className="text-xs text-grayscale-400">≈ $2,819.94</div>
            </div>

            <div className="xl:w-[172px] rounded-lg xl:px-5 xl:py-4 p-3 bg-grayscale-800 space-y-1 flex-shrink-0">
              <div className="text-xs xl:text-sm text-grayscale-400">Total Supply</div>

              <span className="text-brand-magnolia font-medium text-lg xl:text-[24px] tracking-[-0.1px]">
                100,010
              </span>
            </div>

            <div className="xl:w-[172px] rounded-lg xl:px-5 xl:py-4 p-3 bg-grayscale-800 space-y-1 flex-shrink-0">
              <div className="text-xs xl:text-sm text-grayscale-400">Total Volume Traded</div>

              <div className="flex gap-2 items-center">
                <IconSvg icon="bitcoinM" />

                <span className="text-brand-magnolia font-medium text-lg xl:text-[24px] tracking-[-0.1px]">
                  4.34
                </span>
              </div>

              <div className="text-xs text-grayscale-400">≈ $422,018.82</div>
            </div>

            <div className="xl:w-[172px] rounded-lg xl:px-5 xl:py-4 p-3 bg-grayscale-800 space-y-1 flex-shrink-0">
              <div className="text-xs xl:text-sm text-grayscale-400">Owners / Unique</div>

              <span className="text-brand-magnolia font-medium text-lg xl:text-[24px] tracking-[-0.1px]">
                7,887
              </span>
            </div>

            <div className="xl:w-[172px] rounded-lg xl:px-5 xl:py-4 p-3 bg-grayscale-800 space-y-1 flex-shrink-0">
              <div className="text-xs xl:text-sm text-grayscale-400">Listed</div>

              <span className="text-brand-magnolia font-medium text-lg xl:text-[24px] tracking-[-0.1px]">
                26%
              </span>
            </div>

            <div className="xl:w-[172px] rounded-lg xl:px-5 xl:py-4 p-3 bg-grayscale-800 space-y-1 flex-shrink-0">
              <div className="text-xs xl:text-sm text-grayscale-400">Active Raffles</div>

              <span className="text-brand-magnolia font-medium text-lg xl:text-[24px] tracking-[-0.1px]">
                11
              </span>
            </div>
          </div>
        </div>

        <div className="mb-6 space-y-3 xl:hidden">
          <div className="flex gap-2 justify-between items-center w-full xl:gap-3 xl:w-fit">
            <div className="flex rounded-md bg-grayscale-900 w-fit">
              {views.map(view => (
                <button
                  key={view.key}
                  className={`p-2.5 flex items-center justify-center transition-all duration-200 w-fit ${
                    activeView === view.key
                      ? 'bg-grayscale-800 text-brand-tangerine rounded-md'
                      : 'text-grayscale-500'
                  }`}
                  onClick={() => handleViewClick(view.key)}
                >
                  <IconSvg icon={view.icon as Icons} />
                </button>
              ))}
            </div>

            <div className="flex gap-2 items-center xl:gap-3">
              <div className="hidden sm:block">
                <SearchCollectionInput />
              </div>

              <SortByMenu />

              <RaffleFilterMenu />
            </div>
          </div>

          <div className="block w-full sm:hidden">
            <SearchCollectionInput />
          </div>
        </div>

        <div className="grid gap-[18px] grid-cols-[repeat(auto-fit,minmax(320px,1fr))]">
          {collectionDetailRaffles.map((raffle, index) => (
            <RaffleCard
              key={index}
              image={raffle.image}
              title={raffle.title}
              creator={raffle.creator}
              ticketsSold={raffle.ticketsSold}
              ticketsPrice={raffle.ticketsPrice}
            />
          ))}
        </div>
      </section>
    </>
  );
};

export default CollectionDetail;
